import { createRef, type ReactElement, type ReactNode } from 'react';

// Global styles for the react-day-picker calendar used by the Buy Direct
// Checkout date picker. Next.js 12 (Pages Router) only allows global CSS
// imports from the custom App, so the stylesheet lives here rather than the
// component that consumes it.
import 'react-day-picker/style.css';
import { AtomsPortalProvider, AtomsProvider, LinkProvider } from '@opendoor/atoms/utils';
import type { AtomsObservability, RenderLink } from '@opendoor/atoms/utils';
import { BricksProvider } from '@opendoor/bricks-next';
import { AddContextualData, analytics, globalObservability } from '@opendoor/observability/slim';
import type { JourneyEventContextualData } from '@opendoor/observability/slim';
import ErrorBoundaryWithObservability from 'components/ErrorBoundary';
import NavListener from 'components/NavListener';
import { OneTrustScripts } from 'components/shared/OneTrust';
import {
  initObservability,
  OBSERVABILITY_QUERY_PARAM_KEYS,
  useObservability,
} from 'helpers/observability';
import type { GlobalObservabilityContext } from 'helpers/observability';
import type { NextPage } from 'next';
import App from 'next/app';
import type { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import NextLink from 'next/link';
import Script from 'next/script';
import ReactModal from 'react-modal';

import AuthProvider from '../components/account/AuthContextProvider';
import { AddressValidationContextProvider } from '../helpers/AddressValidationContext';
import { APPTOOLBOX_FE_INJECTION_SCRIPT } from '../helpers/apptoolboxInjection';
import '../polyfills/react-createFactory';
// Graphik @font-face (atoms-provided). Imported as a module so Next/webpack
// resolves the relative font url()s and bundles the woff2 files.
import '@opendoor/atoms/styles/fonts';

// Atoms Tailwind output (generated by `pnpm generate:atoms-css`). Imported last
// so its unlayered utilities win in the cascade.
import '../styles/generated/atoms-tw.css';

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

// Routes atoms link-like components through the Next router.
const renderAtomsLink: RenderLink = ({ to, href, ref, children, ...rest }) => (
  <NextLink href={href ?? to ?? ''} ref={ref} {...rest}>
    {children}
  </NextLink>
);

// Adapts cosmos's useObservability hook to the shape AtomsProvider expects
// (an AtomsObservability exposing trackJourneyEvent).
const useAtomsObservability = (): AtomsObservability => {
  const { trackJourneyEvent } = useObservability();
  return { trackJourneyEvent };
};

if (process.env.OUTPUT_BRICKS_CSS) {
  require('../../public/bricks.css');
}
// eslint-disable-next-line @opendoor/no-direct-env-check
if (process.env.NEXT_PUBLIC_DEPLOY_ENV === 'test' && typeof window == 'undefined') {
  require('../../mockTivoServer.ts');
}

interface IProps extends AppPropsWithLayout {
  /** Whether the request is from a bot (from x-od-is-bot header) */
  isBot: boolean;
  /**  path of HTTP header x-od-forwarded-url */
  originPath: string;
  /** HTTP header x-od-forwarded-url */
  originUrl: string;
  /** HTTP header referer */
  referer: string;
  /* path in cosmos */
  cosmosPathname: string;
  /** url.pathname if we show/proxy a different page than requested based on context */
  proxyPath: string;
  /** The url.search that is actually passed to the underlying service */
  proxyQueryMap: Record<string, string>;
  /** Error object passed to page component */
  err?: any;
}
interface IErrorState {
  hasError?: boolean;
  errorEventId?: string;
}

export default class ErrorApp extends App<IProps, IErrorState> {
  override state: IErrorState = {
    hasError: false,
  };

  // Portal target for Atoms overlays (Combobox dropdowns, Dialogs). Atoms
  // overlays portal to <body> with no z-index, so a marketplace panel that owns
  // a stacking context (e.g. the filters panel at `z-2`, the mobile sheet at
  // `z-[3000]`) paints over them. Routing overlays into this high-z-index root
  // keeps them above any page content. See AtomsPortalProvider below.
  private atomsOverlayRoot = createRef<HTMLDivElement>();

  static override async getInitialProps({ Component, ctx }: AppContext) {
    const isBot = (ctx.req?.headers['x-od-is-bot'] as string) === 'true';
    const originUrl = (ctx.req?.headers['x-od-forwarded-url'] as string) ?? '';
    let originPath: string;
    try {
      originPath = new URL(originUrl).pathname;
    } catch {
      originPath = '';
    }

    // Builds Proxy-Path, Proxy-Query-Map from server request header
    type websiteSourceContext = NonNullable<JourneyEventContextualData['websiteSourceContext']>;
    const referer: websiteSourceContext['referrer'] =
      (ctx.req?.headers['referer'] as string) ?? undefined;
    const proxyPath: websiteSourceContext['proxyPath'] =
      (ctx.req?.headers['Proxy-Path'] as string) ?? undefined;
    const proxyQueryMap: websiteSourceContext['proxyQueryMap'] = ctx.req?.headers['proxy-query-map']
      ? Object.fromEntries(
          Object.entries(JSON.parse(ctx.req?.headers['proxy-query-map'] as string)).map(
            ([key, value]) => [key, String(value)],
          ),
        )
      : {};
    let pageProps = {};
    try {
      if (Component.getInitialProps) {
        pageProps = await Component.getInitialProps(ctx);
      }
      return {
        pageProps,
        isBot,
        originPath,
        originUrl,
        referer,
        cosmosPathname: ctx.pathname,
        proxyPath,
        proxyQueryMap,
      };
    } catch (error) {
      const { captureException } = await import('@sentry/nextjs');
      // Capture errors that happen during a page's getInitialProps.
      // This will work on both client and server sides.
      const errorEventId = captureException(error);
      return {
        hasError: true,
        errorEventId,
        pageProps,
        isBot,
        originUrl,
        originPath,
        referer,
        cosmosPathname: ctx.pathname,
        proxyPath,
        proxyQueryMap,
      };
    }
  }

  static getDerivedStateFromProps(props: IErrorState, state: IErrorState) {
    // If there was an error generated within getInitialProps, and we haven't
    // yet seen an error, we add it to this.state here
    return {
      hasError: props.hasError || state.hasError || false,
      errorEventId: props.errorEventId || state.errorEventId || undefined,
    };
  }

  static getDerivedStateFromError() {
    // React Error Boundary here allows us to set state flagging the error (and
    // later render a fallback UI).
    return { hasError: true };
  }
  override async componentDidCatch(error: any) {
    const errorEventId = globalObservability.getSentryClient().captureException?.(error);
    // Store the event id at this point as we don't have access to it within
    // `getDerivedStateFromError`.
    this.setState({ errorEventId });
  }

  override componentDidMount() {
    const { originPath, originUrl, referer, cosmosPathname, proxyPath, proxyQueryMap } = this.props;

    const globalContext: GlobalObservabilityContext = [
      {
        key: 'cosmosPathname',
        value: cosmosPathname,
      },
    ];

    // For allowed query parameters, let's add them to our global observability context
    Object.keys(this.props.router.query)?.forEach((key) => {
      const queryKey = toCamelCase(key);
      const value = this.props.router.query[key];
      if (OBSERVABILITY_QUERY_PARAM_KEYS.includes(queryKey) && value !== undefined) {
        globalContext.push({
          key: queryKey,
          value: `${value}`,
        });
      }
    });

    // Always initialize observability so child components calling trackJourneyEvent/trackError
    // don't throw. Skipping initObservability causes a crash loop in staging where
    // componentDidCatch -> trackError -> throw -> componentDidCatch -> infinite loop.
    initObservability({ globalContext, isBot: this.props.isBot });

    // Skip analytics tracking for bot traffic — bots don't need page views or journey events
    if (!this.props.isBot) {
      // TODO: [GROWEPD-806] Remove — redundant with CF Worker Journey pageViewEvent
      analytics.trackPageView({
        originPath,
        originUrl,
        referer,
        resource: 'cosmos',
      });
      globalObservability.trackJourneyEvent(
        'platform',
        'pageViewEvent',
        {},
        {
          websiteSourceContext: {
            referrer: referer,
            proxyPath,
            proxyQueryMap,
          },
        },
      );
    }
  }

  override render() {
    // Necessary for accessibility. ReactModal uses this to hide (via aria-hidden) the rest of the
    // app while the modal is active.
    // https://reactcommunity.org/react-modal/accessibility/#app-element
    ReactModal.setAppElement('#__next');

    // right now, we're not doing anything with the error. We might want to redirect
    // to /error in the future (or reload the page).
    const { Component, pageProps, originPath, originUrl, referer, err } = this.props;

    // Support per-page layouts via getLayout pattern
    const getLayout = Component.getLayout ?? ((page: ReactElement) => page);

    return (
      <AddContextualData data={{ originPath, originUrl, referer }}>
        <Head>
          {/* Hide 1x1 tracking pixels injected by GTM to prevent layout gaps */}
          <style>{`img[width="1"][height="1"] { display: none; }`}</style>
          <meta
            key="description"
            name="description"
            content="Opendoor is the new way to sell your home. Skip the hassle of listing, showings and months of stress, and close on your own timeline. Get a free offer today!"
          />
          <meta key="og:url" property="og:url" content="https://opendoor.com" />
          <meta key="og:type" property="og:type" content="website" />
          <meta
            key="og:title"
            property="og:title"
            content="Opendoor | Sell your home the minute you’re ready."
          />
          <meta
            key="og:description"
            property="og:description"
            content="Opendoor is the new way to sell your home. Skip the hassle of listing, showings and months of stress, and close on your own timeline. Get a free offer today!"
          />
          <meta
            key="og:image"
            property="og:image"
            content="https://images.opendoor.com/source/s3/imgdrop-production/2024-06-1719377665829-19976.jpg?preset=square-1024&service=cosmos&dpr=2"
          />
          <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
          <meta key="twitter:domain" property="twitter:domain" content="opendoor.com" />
          <meta key="twitter:url" property="twitter:url" content="https://opendoor.com" />
          <meta
            key="twitter:title"
            name="twitter:title"
            content="Opendoor | Sell your home the minute you’re ready."
          />
          <meta
            key="twitter:description"
            name="twitter:description"
            content="Opendoor is the new way to sell your home. Skip the hassle of listing, showings and months of stress, and close on your own timeline. Get a free offer today!"
          />
          <meta
            key="twitter:image"
            name="twitter:image"
            content="https://images.opendoor.com/source/s3/imgdrop-production/4c68636ad1df40d08a114638883c30d0.png?preset=square-2048"
          />
        </Head>
        {/* eslint-disable-next-line @next/next/inline-script-id */}
        <Script
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{ __html: APPTOOLBOX_FE_INJECTION_SCRIPT }}
        />
        <OneTrustScripts />
        <div id="x-od-app-toolbox-root"></div>
        {/* Portal target for Atoms overlays — kept above all page content so
            Combobox dropdowns and Dialogs are never covered by panels that own
            a stacking context. Zero-size; portaled children position themselves. */}
        <div
          id="atoms-overlay-root"
          ref={this.atomsOverlayRoot}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 100000 }}
        ></div>
        <ErrorBoundaryWithObservability>
          <NavListener />
          <AuthProvider>
            <BricksProvider useObservability={useObservability}>
              <AtomsProvider useObservability={useAtomsObservability}>
                <AtomsPortalProvider container={this.atomsOverlayRoot}>
                  <LinkProvider renderLink={renderAtomsLink}>
                    <AddressValidationContextProvider>
                      {getLayout(<Component {...pageProps} err={err} />)}
                    </AddressValidationContextProvider>
                  </LinkProvider>
                </AtomsPortalProvider>
              </AtomsProvider>
            </BricksProvider>
          </AuthProvider>
        </ErrorBoundaryWithObservability>
      </AddContextualData>
    );
  }
}

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}
